import classNames from 'classnames';
import React, { useState } from 'react';
import { useCreateInvoicePdf } from '../../../hooks/apolloHooks/useCreateInvoicePdf';
import { CardInvoice } from '../../../integrations/allkort/graphql/interface/Customer';
import { ImportIcon } from '../../svgs/ExpressonSVGs';

type InvoiceTableRowProps = {
    wrapperClasses?: string;
    invoice: CardInvoice & { paid?: number; remains?: number };
}

const InvoiceTableRow: React.FC<InvoiceTableRowProps> = ({
    wrapperClasses = '',
    invoice
}) => {

    const [invoicePdfUrl, setInvoicePdfUrl] = useState<string>('');
    const { createInvoicePdf, loading: pdfLoading } = useCreateInvoicePdf();

    // Handle action button click - fetch PDF and open in new tab
    const handleDownloadClick = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // If we already have the URL, open it
        if (invoicePdfUrl) {
            window.open(invoicePdfUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        // Fetch the PDF URL
        try {
            const result = await createInvoicePdf(invoice.id);
            if (result?.url) {
                setInvoicePdfUrl(result.url);
                // Open the URL immediately after fetching
                window.open(result.url, '_blank', 'noopener,noreferrer');
            }
        } catch (err) {
            console.error('Failed to fetch invoice PDF URL:', err);
        }
    };

    // Format date to Swedish locale
    const formatDate = (dateString: string | null): string => {
        if (!dateString) return '-';
        const date = new Date(dateString).toLocaleDateString('sv-SE');
        return date;
    };

    // Format amount as currency
    const formatAmount = (amount: number | null): string => {
        if (amount === null || amount === undefined) return '0 kr';
        return (amount || 0).toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kr';
    };

    // Determine invoice status
    const getStatus = (returnClassName = false): string => {
        // Check if paid (remains must be explicitly 0)
        if (invoice.remains === 0) {
            return returnClassName ? 'status-paid' : 'Betald';
        }

        // Check if overdue with grace period
        if (invoice.dueDate) {
            const dueDate = new Date(invoice.dueDate);
            dueDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Apply grace period (1 day)
            const gracePeriodDeadline = new Date(dueDate);
            gracePeriodDeadline.setDate(gracePeriodDeadline.getDate() + 1);

            // If today is after grace period deadline and invoice is not paid, it's overdue
            if (today > gracePeriodDeadline) {
                return returnClassName ? 'status-overdue' : 'Förfallen';
            }
        }

        return returnClassName ? 'status-unpaid' : 'Obetald';
    };

    const displayAmount = invoice.remains === 0 || invoice.remains === undefined
        ? invoice.amount
        : invoice.remains;

    return (
        <div
            className={classNames(
                'invoice-table-single-row',
                wrapperClasses
            )}
            id={`invoice-${invoice.id}`}
        >
            <span className="row-invoicenr" data-label="Fakturanummer" aria-label={`Fakturanummer: ${invoice.id}`}>{invoice.id}</span>
            <span className="row-date" data-label="Fakturadatum" aria-label={`Fakturadatum: ${formatDate(invoice.invoiceDate)}`}>{formatDate(invoice.invoiceDate)}</span>
            <span className="row-due" data-label="Förfallodatum" aria-label={`Förfallodatum: ${formatDate(invoice.dueDate)}`}>{formatDate(invoice.dueDate)}</span>
            <span className="row-amount" data-label="Belopp" aria-label={`Belopp: ${formatAmount(displayAmount)}`}>{formatAmount(displayAmount)}</span>
            <span className={classNames("row-status", getStatus(true))} data-label="Status" aria-label={`Status: ${getStatus()}`}>{getStatus()}</span>
            <span
                className={classNames("row-action", { "loading": pdfLoading })}
                data-label="Åtgärd"
                onClick={handleDownloadClick}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleDownloadClick(e);
                    }
                }}
                aria-label={`Visa faktura med id: ${invoice.id}`}
                style={{ cursor: 'pointer' }}
            >
                {pdfLoading ? '...' : 'Visa'}<ImportIcon width="18" height="18" />
            </span>
        </div>
    )
}

export default InvoiceTableRow
